import type {PendingInteraction} from './types'

/**
 * Creates a patched fetch that tracks pending requests for the active interaction.
 * Returns the patched function to assign to window.fetch.
 */
export function createPatchedFetch(
  originalFetch: typeof window.fetch,
  getPending: () => PendingInteraction | null,
  onRequestComplete: (interaction: PendingInteraction) => void,
): typeof window.fetch {
  return function (this: typeof globalThis, ...args: Parameters<typeof fetch>): Promise<Response> {
    const interaction = getPending()
    if (!interaction || interaction.settled) {
      return originalFetch.apply(this, args)
    }

    const url = typeof args[0] === 'string' ? args[0] : args[0] instanceof Request ? args[0].url : ''

    if (shouldIgnoreUrl(url)) {
      return originalFetch.apply(this, args)
    }

    const endpoint = extractEndpoint(url)

    interaction.pendingRequests++
    interaction.networkRequestCount++
    interaction.hadActivity = true
    if (endpoint && interaction.endpoints.length < 5) interaction.endpoints.push(endpoint)

    // Record when the first request fires
    if (interaction.networkStartTime === null) {
      interaction.networkStartTime = performance.now()
    }

    if (interaction.endTimer) {
      clearTimeout(interaction.endTimer)
      interaction.endTimer = undefined
    }

    // eslint-disable-next-line github/no-then -- wrapping fetch return value in patched implementation
    return originalFetch.apply(this, args).then(
      response => {
        interaction.networkEndTime = Math.max(interaction.networkEndTime, performance.now())
        onRequestComplete(interaction)
        return response
      },
      error => {
        interaction.networkEndTime = Math.max(interaction.networkEndTime, performance.now())
        onRequestComplete(interaction)
        throw error
      },
    )
  }
}

// Extract a short endpoint path from a URL (strip origin, query, hash)
export function extractEndpoint(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.origin !== window.location.origin) return ''
    return parsed.pathname
  } catch {
    return ''
  }
}

// URLs that should not count as interaction activity (telemetry, assets, etc.)
export function shouldIgnoreUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin)
    const path = parsed.pathname
    if (path.endsWith('/collect') || path.endsWith('/stats')) return true
    if (parsed.origin === 'https://github.githubassets.com' && path.startsWith('/assets')) return true
    return false
  } catch {
    return false
  }
}
